import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Sort, Filter, Search, Toolbar, VirtualScroll } from '@syncfusion/ej2-react-grids';
import { DatePickerComponent } from '@syncfusion/ej2-react-calendars';

import SearchBar from '../utils/SearchBar';
import CustomSelect from '../utils/CustomSelect';

const ListDommage = ({ lstDommages, showAddButton = true, showFilter = true, columns = [], filteredFields = [], setSelectedRow = null, handleRowDoubleClick = null, size = 550, loading = false }) => {
    const navigate = useNavigate();
    const routeParams = useParams();

    console.log(routeParams);

    const declaUUID = routeParams?.uuid || null;

    const statusTemplate = (props) => {
        const normalizedStatus = props.Etat.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

        const statusColors = {
            'garanti': 'bg-green-500',
            'non garanti': 'bg-red-500',
            'en attente': 'bg-yellow-500',
            'pre-rapport': 'bg-blue-500',
            'devis valide': 'bg-purple-500',
            'ouvert': 'bg-green-500',
            'sans suite': 'bg-red-700',
        };

        let statusColor = 'bg-white';
        for (const [key, value] of Object.entries(statusColors)) {
            if (normalizedStatus.startsWith(key)) {
                statusColor = value;
                break;
            }
        }

        return (
            <span className={`px-2 py-1 rounded text-white ${statusColor}`}>
                {props.Etat}
            </span>
        );
    };

    function DefautTemplate(props) {
        if (props.Archiver_Enr) {
            return (<div>
                <i className="text-blue-500 fa-square-check fas"></i>
            </div>);
        } else {
            return (<div>
                <i className="w-15 h-15 text-blue-500 fa-square far"></i>
            </div>);
        }
    }

    const emptyMessageTemplate = useCallback(() => {
        if (loading) {
            return (
                <div>
                    {Array.from({ length: 15 }).map((_, i) => (
                        <div key={i} className="flex flex-row gap-4 p-3 border-b-2 text-gray-500">
                            {Array.from({ length: 5 }).map((_, j) => (
                                <div key={j} className="bg-gray-200 rounded-md h-4 animate-pulse" style={{ width: `${Math.random() * 30 + 10}rem` }}></div>
                            ))}
                        </div>
                    ))}
                </div>
            );
        }
        return <div className="p-4 text-gray-500 text-start">Aucun résultat trouvé.</div>;
    }, [loading]);

    const gridRef = useRef();

    const [filtersVisible, setFiltersVisible] = useState(false);

    const [query, setQuery] = useState("");

    const norm = (s) =>
        (s ?? "")
            .toString()
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");

    const SEARCH_FIELDS = ['Libelle_Court', 'Nom_Site', 'Libelle_Declaration', 'Code_Procedure', 'Charge_Affaire', 'CP', 'Ville', 'Me_Ouvrage', 'Code_Compta', 'Cd_Patho_1', 'Cd_Patho_2', 'Cd_Patho_3'];

    const [selectedSociete, setSelectedSociete] = useState("Toutes");
    const [selectedEntite, setSelectedEntite] = useState("Toutes");
    const [selectedEtat, setSelectedEtat] = useState("Toutes");
    const [selectedType, setSelectedType] = useState("Toutes");
    const [selectedTypeOuvrage, setSelectedTypeOuvrage] = useState("Toutes");
    const [selectedTypeMarche, setSelectedTypeMarche] = useState("Toutes");

    const [selectedMinDate, setSelectedMinDate] = useState(null);
    const [selectedMaxDate, setSelectedMaxDate] = useState(null);

    const [showWithCost, setShowWithCost] = useState(false);
    const [hideArchived, setHideArchived] = useState(false);
    const [hideCompleted, setHideCompleted] = useState(false);

    const [filteredData, setFilteredData] = useState([]);

    if (!handleRowDoubleClick) {
        handleRowDoubleClick = (event) => {
            const clickedRowData = event.rowData;
            const uuid = clickedRowData.UUID_;
            if (uuid) navigate(`/dommages/${uuid}`);
        };
    }

    useEffect(() => {
        let filtered = lstDommages;

        if (query.trim()) {
            const q = norm(query);
            filtered = filtered.filter((d) =>
                SEARCH_FIELDS.some((f) => norm(d?.[f]).includes(q))
            );
        }

        if (selectedSociete !== "Toutes") { filtered = filtered.filter(declaration => declaration.Societe === selectedSociete); }
        if (selectedEntite !== "Toutes") { filtered = filtered.filter(declaration => declaration.Entite === selectedEntite); }
        if (selectedEtat !== "Toutes") { filtered = filtered.filter(declaration => declaration.Etat === selectedEtat); }
        if (selectedType !== "Toutes") { filtered = filtered.filter(declaration => declaration.Type_Declaration === selectedType); }
        if (selectedTypeOuvrage !== "Toutes") { filtered = filtered.filter(declaration => declaration.Type_Ouvrage === selectedTypeOuvrage); }
        if (selectedTypeMarche !== "Toutes") { filtered = filtered.filter(declaration => declaration.Type_Marche === selectedTypeMarche); }

        if (selectedMinDate) {
            filtered = filtered.filter(declaration => new Date(declaration.Date_Declaration) >= selectedMinDate);
        }
        if (selectedMaxDate) {
            filtered = filtered.filter(declaration => new Date(declaration.Date_Declaration) <= selectedMaxDate);
        }

        if (showWithCost) filtered = filtered.filter(d => d.Recours_TT !== 0 || d.Recours_PP !== 0);
        // if (hideArchived) filtered = filtered.filter(d => !d.Archiver_Enr);
        // if (hideCompleted) {
        //     const completedStates = ["cloture", "sans suite"];
        //     filtered = filtered.filter(d => !completedStates.some(state => d.Etat.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').startsWith(state)));
        // }

        setFilteredData(filtered);
    }, [query, selectedEntite, selectedEtat, selectedType, selectedTypeMarche, selectedTypeOuvrage, selectedMaxDate, selectedMinDate, showWithCost, hideArchived, hideCompleted, lstDommages]);

    const optSociete = ["Toutes", ...new Set(filteredData.map(declaration => declaration.Societe).filter(societe => societe))];
    const optEntite = ["Toutes", ...new Set(filteredData.map(declaration => declaration.Entite).filter(entite => entite))];
    const optEtat = ["Toutes", ...new Set(filteredData.map(declaration => declaration.Etat).filter(etat => etat))];
    const optType = ["Toutes", ...new Set(filteredData.map(declaration => declaration.Type_Declaration).filter(type => type))];
    const optTypeOuvrage = ["Toutes", ...new Set(filteredData.map(declaration => declaration.Type_Ouvrage).filter(type => type))];
    const optTypeMarche = ["Toutes", ...new Set(filteredData.map(declaration => declaration.Type_Marche).filter(type => type))];

    if (columns.length === 0) {
        columns = [
            { field: "Libelle_Court", headerText: "Libelle", width: "300px" },
            { field: "Defaut", headerText: "Défaut", width: "100px", template: DefautTemplate, textAlign: "center" },
            { field: "Etat", headerText: "État", width: "150px", template: statusTemplate },
            { field: "Nom_Site", headerText: "Site", width: "200px" },
            { field: "Libelle_Declaration", headerText: "Déclaration", width: "200px" },
            { field: "Code_Procedure", headerText: "Code Procédure", width: "150px" },
            { field: "Date_DOC", headerText: "Date DOC", type: "date", format: "dd/MM/yyyy", width: "130px" },
            { field: "Date_PVRecep", headerText: "Date PV Réception", type: "date", format: "dd/MM/yyyy", width: "150px" },
            { field: "Charge_Affaire", headerText: "Chargé d'Affaire", width: "200px" },
            { field: "Entite", headerText: "Entité", width: "150px" },
            { field: "Societe", headerText: "Société", width: "150px" },
            { field: "CP", headerText: "CP", width: "100px" },
            { field: "Ville", headerText: "Ville", width: "150px" },
            { field: "Me_Ouvrage", headerText: "Maître d'Ouvrage", width: "200px" },
            { field: "Code_Compta", headerText: "Code Comptable", width: "150px" },
            { field: "Type_Ouvrage", headerText: "Type d'Ouvrage", width: "180px" },
            { field: "Recours_TT", headerText: "Recours_TT", width: "150px" },
            { field: "Recours_PP", headerText: "Recours_PP", width: "150px" },
            { field: "Cd_Patho_1", headerText: "Fiche", width: "150px" },
            { field: "Cd_Patho_2", headerText: "Titre", width: "150px" },
            { field: "Cd_Patho_3", headerText: "Chapitres", width: "150px" },
            { field: "_Date_Modif", headerText: "Modifié le", type: "date", format: "dd/MM/yyyy", width: "130px" },
            { field: "_Date_Crea", headerText: "Créé le", type: "date", format: "dd/MM/yyyy", width: "130px" },
        ];
    }

    //const filteredColumns = filteredFields.length > 0 ? filteredFields.map(field => columns.find(col => col.field === field)).filter(col => col !== undefined) : columns;

    const formattedData = useMemo(() => {
        return filteredData.map(dommage => ({
            ...dommage,
        }));
    }, [filteredData]);

    useEffect(() => {
        if (gridRef.current && Array.isArray(formattedData)) {
            gridRef.current.dataSource = formattedData;
            gridRef.current.dataBind();
        }
    }, [formattedData]);

    // console.log(lstDommages)

    return (
        <div className="bg-white p-4 rounded-lg">
            <div className="flex lg:flex-row flex-col justify-between items-start lg:items-center mx-auto my-4 lg:my-8 px-6 pb-4 border-gray-300 border-b container">
                <div>
                    <h4 className="font-bold text-gray-800 text-2xl leading-tight">Dommages</h4>
                    <ul className="flex md:flex-row flex-col items-start md:items-center mt-3 text-gray-600 text-sm">
                        <li className="flex items-center mt-3 md:mt-0 mr-3">
                            <span className="mr-2">
                                <i className="fa-fire-extinguisher fas"></i>
                            </span>
                            <div className="flex items-center gap-1">
                                {loading ? (
                                    <div className="bg-gray-200 rounded-md w-9 h-4 animate-pulse"></div>
                                ) : (
                                    <span>{filteredData.length}</span>
                                )}
                                {' '}{filteredData.length === 1 ? 'Dommages' : 'Dommages'}
                            </div>
                        </li>
                    </ul>
                </div>
                {showAddButton && (
                    <div className="mt-6 lg:mt-0">
                        <Link
                            to="/dommages/create"
                            state={declaUUID ? { declaUUID: declaUUID } : null}
                            className="bg-orange-600 hover:bg-orange-500 px-8 py-2 rounded focus:outline-none focus:ring-2 focus:ring-orange-700 focus:ring-offset-2 text-white text-sm transition duration-300 ease-in-out"
                        >
                            Ajouter un Dommage
                        </Link>
                    </div>
                )}
            </div>
            <div className="lg:mx-auto mb-4 lg:mb-8 px-6 container">
                <div class={`${showFilter == true ? 'block' : 'hidden'}`}>
                    <div className="flex justify-between mb-4">
                        <button
                            onClick={() => setFiltersVisible(!filtersVisible)}
                            className="flex items-center text-gray-500 hover:text-orange-600 text-sm"
                        >
                            {filtersVisible ? (
                                <i className="fa-chevron-up fas"></i>
                            ) : (
                                <i className="fa-chevron-down fas"></i>
                            )}
                            <span className="ml-2">{filtersVisible ? 'Réduire' : 'Afficher'} les filtres</span>
                        </button>
                        <div className="w-[20rem]">
                            <SearchBar
                                value={query}
                                onChange={setQuery}
                                onClear={() => setQuery("")}
                                placeholder="Rechercher …"
                                disabled={loading}
                            />
                        </div>
                    </div>
                    {filtersVisible && (
                        <div id="cont_filtre" className="flex xl:flex-row flex-col gap-4 pb-6 w-full">
                            <div className="flex md:flex-row flex-col gap-4 w-full xl:w-1/2">
                                <div className="w-full">
                                    <CustomSelect
                                        label={"Société"}
                                        options={optSociete}
                                        value={selectedSociete}
                                        onChange={setSelectedSociete}
                                    />
                                    <CustomSelect
                                        label={"Entité/Région"}
                                        options={optEntite}
                                        value={selectedEntite}
                                        onChange={setSelectedEntite}
                                    />
                                </div>
                                <div className="w-full">
                                    <CustomSelect
                                        label={"État"}
                                        options={optEtat}
                                        value={selectedEtat}
                                        onChange={setSelectedEtat}
                                    />
                                    <CustomSelect
                                        label={"Type de Déclaration"}
                                        options={optType}
                                        value={selectedType}
                                        onChange={setSelectedType}
                                    />
                                </div>
                            </div>
                            <div className="flex md:flex-row flex-col items-center gap-4 w-full xl:w-1/2 md:full">
                                <div className="w-full">
                                    <CustomSelect
                                        label={"Type de Marché"}
                                        options={optTypeMarche}
                                        value={selectedTypeMarche}
                                        onChange={setSelectedTypeMarche}
                                    />
                                    <CustomSelect
                                        label={"Type d'Ouvrage"}
                                        options={optTypeOuvrage}
                                        value={selectedTypeOuvrage}
                                        onChange={setSelectedTypeOuvrage}
                                    />
                                </div>
                                <div className="flex flex-col justify-between w-full h-full">
                                    <div>
                                        <span className="text-gray-600 text-sm">À partir du :</span>
                                        <DatePickerComponent
                                            placeholder="Trier par Date Minimal"
                                            format={"dd/MM/yyyy"}
                                            value={selectedMinDate}
                                            onChange={(e) => setSelectedMinDate(e.value)}
                                        />
                                    </div>
                                    <div>
                                        <span className="text-gray-600 text-sm">Jusqu'au :</span>
                                        <DatePickerComponent
                                            placeholder="Trier par Date Maximal"
                                            format={"dd/MM/yyyy"}
                                            value={selectedMaxDate}
                                            onChange={(e) => setSelectedMaxDate(e.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-row xl:flex-col justify-center gap-10 xl:gap-0 w-full xl:w-1/4">
                                <label class="mcui-checkbox">
                                    <input type="checkbox" checked={showWithCost} onChange={() => setShowWithCost(!showWithCost)} />
                                    <div>
                                        <svg class="mcui-check" viewBox="-2 -2 35 35" aria-hidden="true">
                                            <polyline points="7.57 15.87 12.62 21.07 23.43 9.93" />
                                        </svg>
                                    </div>
                                    <div class="text-gray-600 text-lg">Avec Coût</div>
                                </label>
                                {/* <label class="mcui-checkbox">
                                    <input type="checkbox" checked={hideArchived} onChange={() => setHideArchived(!hideArchived)} />
                                    <div>
                                        <svg class="mcui-check" viewBox="-2 -2 35 35" aria-hidden="true">
                                            <polyline points="7.57 15.87 12.62 21.07 23.43 9.93" />
                                        </svg>
                                    </div>
                                    <div class="text-gray-600 text-lg">Masquer les Archives</div>
                                </label>
                                <label class="mcui-checkbox">
                                    <input type="checkbox" checked={hideCompleted} onChange={() => setHideCompleted(!hideCompleted)} />
                                    <div>
                                        <svg class="mcui-check" viewBox="-2 -2 35 35" aria-hidden="true">
                                            <polyline points="7.57 15.87 12.62 21.07 23.43 9.93" />
                                        </svg>
                                    </div>
                                    <div class="text-gray-600 text-lg">Masquer les Terminés</div>
                                </label> */}
                            </div>
                        </div>

                    )}
                </div>

                <div className="border-2 border-gray-300 hover:border-orange-400 rounded w-full transition duration-300 ease-in-out grow">
                    <GridComponent
                        ref={gridRef}
                        dataSource={formattedData}
                        allowSorting={true}
                        recordDoubleClick={handleRowDoubleClick}
                        emptyRecordTemplate={emptyMessageTemplate}
                        enableStickyHeader={true}
                        enableVirtualization={true}
                        height={size}
                        loadingIndicator={{ indicatorType: 'Shimmer' }}
                        sortSettings={{
                            columns: [{ field: '_Date_Crea', direction: 'Descending' }]
                        }}
                        rowSelected={(args) => {
                            if (setSelectedRow) {
                                formattedData.map((item, index) => {
                                    if (item.UUID_ === args.data.UUID_) {
                                        setSelectedRow(item);
                                    }
                                });
                            }
                        }}

                    >
                        <ColumnsDirective>
                            {columns.map(col => (
                                <ColumnDirective key={col.field} {...col} />
                            ))}
                        </ColumnsDirective>
                        <Inject services={[Sort, Filter, Search, Toolbar, VirtualScroll]} />
                    </GridComponent>
                </div>
            </div>
        </div>
    );
};

export default ListDommage;
